import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useAtom, useSetAtom } from 'jotai'
import { useAtomValueLoadable } from 'src/utils/jotai'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { isEqual } from 'lodash'
import { SearchEntity } from 'src/v2/types/core'
import { AMQueryParams } from 'src/v2/types/url'
import { flattenObject } from 'src/utils/flatten-object'
import { browserLogger } from 'src/utils/browser-logger'
import { Circle, Polygon } from 'src/common-services'

import * as atoms from 'src/v2/state/core'
import * as searchAtoms from 'src/v2/state/map-explorer/search'
import * as searchRecoilAtoms from 'src/atoms/search'
import * as areaRecoilAtoms from 'src/atoms/custom-area'
import { currentUser } from 'src/atoms/user'

const LOGIN_PATHS = ['/login', '/sign-up', '/logout']

const checkObjEmpty = (obj: object) => {
  const flat = flattenObject(obj)
  const keys = Object.keys(flat)
  let empty = true
  let i = 0
  while (i < keys.length && empty) {
    if (flat[keys[i]]) {
      empty = false
    }
    i++
  }
  return empty
}

/* 
  This is a better sync since it is unidirectional i.e. parse url -> sync jotai state + recoil state
  All navigation within map-explorer should route with path + query params 
*/
type MapExplorerURLSyncProps = {
  syncOwnership?: boolean // if true, allow sync of ownership ids i.e. id.am-oen, id.am-opa
}
const MapExplorerURLSync = (props: MapExplorerURLSyncProps) => {
  const { syncOwnership = false } = props
  const user = useRecoilValue(currentUser)
  const router = useRouter()

  // Jotai base state
  const [searchEntity, setSearchEntity] = useAtom(atoms.searchEntity)
  const selectedEntityLoadable = useAtomValueLoadable(atoms.selectedEntity)
  const selectedEntity = selectedEntityLoadable.state === 'hasData' ? selectedEntityLoadable.data : undefined
  const setSearchResultType = useSetAtom(searchAtoms.searchResultType)

  // Recoil state, (hack: sync for now)
  const setSelectedAlphamapLocationId = useSetRecoilState(searchRecoilAtoms.selectedLocationId)
  const setInternalGooglePlaceId = useSetRecoilState(searchRecoilAtoms.internalGooglePlaceId)
  const setCustomPolygon = useSetRecoilState(areaRecoilAtoms.polygon)
  const setCustomCircle = useSetRecoilState(areaRecoilAtoms.circle)

  const checkSearchEntityEmpty = useCallback((entity: SearchEntity) => {
    const flat = flattenObject(entity)
    const keys = Object.keys(flat)
    let empty = true
    let i = 0
    while (i < keys.length && empty) {
      if (flat[keys[i]]) {
        empty = false
      }
      i++
    }
    return empty
  }, [])

  const syncURLToState = useCallback(async () => {
    // Logout (should redirect to login and if they try logging in they should get a 402 error)
    if (!LOGIN_PATHS.includes(router.pathname) && (!user || !user.enabled)) {
      browserLogger.warn('❌ User is disabled for path', router.pathname)
      await router.push('/logout')
      return
    }
    browserLogger.info('✅ User is enabled for path', router.pathname)
    // Check if there is a lookup param in the URL and early exit
    // useSearchFromUrlParam should handle this
    if (router.query['lookup']) {
      return
    }

    const params: AMQueryParams = {
      'id.am-loc': router.query['id.am-loc'] ? decodeURIComponent(router.query['id.am-loc'] as string) : undefined,
      'id.google': router.query['id.google'] ? decodeURIComponent(router.query['id.google'] as string) : undefined,
      'id.am-brand': router.query['id.am-brand']
        ? decodeURIComponent(router.query['id.am-brand'] as string)
        : undefined,
      'id.am-opa': router.query['id.am-opa'] ? decodeURIComponent(router.query['id.am-opa'] as string) : undefined,
      'id.am-oen': router.query['id.am-oen'] ? decodeURIComponent(router.query['id.am-oen'] as string) : undefined,
    }

    // Check if the search entity ids are the same as the params and early return if they are
    if (
      isEqual(searchEntity.ids, {
        'am-loc': params['id.am-loc'],
        google: params['id.google'],
        'am-brand': params['id.am-brand'],
        'am-opa': params['id.am-opa'],
        'am-oen': params['id.am-oen'],
      })
    ) {
      return
    }

    // If no query params AND search entity ids empty; return
    if (checkObjEmpty(params) && checkSearchEntityEmpty(searchEntity)) {
      // If ownership sync is disabled, remove non-location params if they exist in the URL
      const mapExplorerKeys = ['id.google', 'id.am-brand']
      const ownershipKeys = ['id.am-oen', 'id.am-opa']
      const sharedKeys = ['id.am-loc']
      const supportedKeys = syncOwnership
        ? [...mapExplorerKeys, ...ownershipKeys, ...sharedKeys]
        : [...mapExplorerKeys, ...sharedKeys]
      const nonMapExplorerParams = Object.keys(router.query).filter((key) => !supportedKeys.includes(key))
      browserLogger.debug('Non map explorer params', nonMapExplorerParams)
      // Remove non-location params if they exist in the URL
      if (nonMapExplorerParams.length) {
        const path = router.asPath.split('?')[0]
        const hash = router.asPath.split('#')[1]
        await router.push({
          pathname: path,
          query: {},
          hash: hash ? `#${hash}` : undefined,
        })
      }
      return
    }

    const obj: SearchEntity = {
      ...searchEntity,
    }
    obj.ids = {
      ...obj.ids,
      'am-loc': params['id.am-loc'] ? decodeURIComponent(params['id.am-loc']) : undefined,
    }
    obj.ids = {
      ...obj.ids,
      google: params['id.google'] ? decodeURIComponent(params['id.google']) : undefined,
    }
    obj.ids = {
      ...obj.ids,
      'am-brand': params['id.am-brand'] ? decodeURIComponent(params['id.am-brand']) : undefined,
    }
    obj.address = params.address ? decodeURIComponent(params.address) : undefined

    // TODO(ss): Support geoms
    // const geom = params.geom ? (JSON.parse(decodeURIComponent(params.geom)) as Polygon | Circle) : undefined
    // if (geom) {
    //   if ('coordinates' in geom) {
    //     obj.geometry = geom as Polygon
    //   } else {
    //     obj.geometry = geom as Circle
    //   }
    // }

    obj.ids = {
      ...obj.ids,
      'am-oen': params['id.am-oen'] ? decodeURIComponent(params['id.am-oen']) : undefined,
    }
    obj.ids = {
      ...obj.ids,
      'am-opa': params['id.am-opa'] ? decodeURIComponent(params['id.am-opa']) : undefined,
    }

    setSearchEntity(obj)
  }, [router.query, router.asPath, searchEntity, syncOwnership])

  useEffect(() => {
    void syncURLToState()
  }, [syncURLToState])

  // Hack: copy jotai values -> recoil
  useEffect(() => {
    const sync = async () => {
      browserLogger.debug('Syncing search entity to recoil state')
      if (!selectedEntity) {
        return
      }
      switch (selectedEntity.type) {
        case 'SELECTED_LOCATION':
        case 'SELECTED_SHOPPING_CENTER':
          browserLogger.debug('Syncing search.ids.am-loc to recoil state')
          setSelectedAlphamapLocationId(selectedEntity.data['am-loc'].id)
          // We additonally set google place id since its info is used in certain parts of the app
          // In the future, we can refactor these parts to use jotai state — searchEntity (it will have associated googlePlace info + more stuff if we fetch it)
          setInternalGooglePlaceId(selectedEntity.data['google'].place_id)
          setSearchResultType('location')
          break
        case 'SELECTED_ADDRESS':
          browserLogger.debug('Syncing search.ids.google to recoil state')
          setInternalGooglePlaceId(selectedEntity.data.place_id)
          setSearchResultType('address')
          break
        case 'SELECTED_BRAND':
          browserLogger.debug('Syncing search.ids.am-brand to recoil state')
          setSearchResultType('brand-profile')
          break
        case 'SELECTED_AREA':
          browserLogger.debug('Syncing search.geometry to recoil state')
          if ('coordinates' in selectedEntity.data) {
            setCustomPolygon(selectedEntity.data as Polygon)
            setSearchResultType('area')
          } else {
            setCustomCircle(selectedEntity.data as Circle)
            setSearchResultType('area')
          }
        case 'SELECTED_OWNER':
          browserLogger.debug('Syncing search.ids.am-opa to recoil state')
          setSearchResultType('owner')
        case 'SELECTED_PARCEL':
          browserLogger.debug('Syncing search.ids.am-opa to recoil state')
          setSearchResultType('parcel')
          break
        default:
          browserLogger.debug('No sync needed for selected entity', selectedEntity)
          break
      }
    }
    void sync()
  }, [selectedEntity, router.asPath])

  return null
}

export const URLSync = () => {
  const router = useRouter()

  if (router.asPath.includes('owner-reveal')) {
    return <MapExplorerURLSync syncOwnership />
  }
  return <MapExplorerURLSync />
}
